/**
 * Trend Analysis and Forecasting Utility
 * Analyzes business metrics trends and provides forecasting
 */

/**
 * Calculate linear regression for trend analysis
 */
const calculateLinearRegression = (data) => {
  if (!Array.isArray(data) || data.length < 2) {
    return { slope: 0, intercept: 0, r2: 0 }
  }

  const n = data.length
  let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0, sumYY = 0

  data.forEach((point, index) => {
    const x = index
    const y = point.value || 0
    sumX += x
    sumY += y
    sumXY += x * y
    sumXX += x * x
    sumYY += y * y
  })

  const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX)
  const intercept = (sumY - slope * sumX) / n
  
  // Calculate R-squared (coefficient of determination)
  const yMean = sumY / n
  let ssRes = 0, ssTot = 0
  
  data.forEach((point, index) => {
    const x = index
    const y = point.value || 0
    const yPred = slope * x + intercept
    ssRes += Math.pow(y - yPred, 2)
    ssTot += Math.pow(y - yMean, 2)
  })
  
  const r2 = ssTot === 0 ? 1 : 1 - (ssRes / ssTot)

  return { slope, intercept, r2: Math.max(0, Math.min(1, r2)) }
}

/**
 * Analyze revenue trend over time
 */
export const analyzeRevenueTrend = (historicalData) => {
  if (!Array.isArray(historicalData) || historicalData.length < 3) {
    return {
      trend: 'insufficient_data',
      strength: 'unknown',
      direction: 'neutral',
      forecast: [],
      insights: []
    }
  }

  // Prepare data for regression
  const revenueData = historicalData
    .filter(d => d.revenue != null)
    .map((d, index) => ({
      month: d.month || `Month ${index + 1}`,
      value: d.revenue,
      date: d.date || new Date()
    }))
    .sort((a, b) => new Date(a.date) - new Date(b.date))

  if (revenueData.length < 3) {
    return {
      trend: 'insufficient_data',
      strength: 'unknown', 
      direction: 'neutral',
      forecast: [],
      insights: []
    }
  }

  const regression = calculateLinearRegression(revenueData)
  const { slope, intercept, r2 } = regression

  // Determine trend direction
  let direction = 'stable'
  if (slope > 1000) direction = 'growing'
  else if (slope < -1000) direction = 'declining'

  // Determine trend strength based on R-squared
  let strength = 'weak'
  if (r2 > 0.7) strength = 'strong'
  else if (r2 > 0.4) strength = 'moderate'

  // Generate 3-month forecast
  const lastIndex = revenueData.length - 1
  const forecast = []
  for (let i = 1; i <= 3; i++) {
    const forecastValue = slope * (lastIndex + i) + intercept
    forecast.push({
      month: `Forecast ${i}`,
      value: Math.max(0, Math.round(forecastValue)),
      confidence: Math.round(r2 * 100)
    })
  }

  // Generate insights
  const insights = generateTrendInsights(direction, strength, slope, r2, revenueData)

  return {
    trend: direction,
    strength,
    direction,
    slope: Math.round(slope),
    r2: Math.round(r2 * 100),
    forecast,
    insights,
    currentValue: revenueData[revenueData.length - 1]?.value || 0,
    regression
  }
}

/**
 * Analyze profit margin trend
 */
export const analyzeProfitMarginTrend = (historicalData) => {
  if (!Array.isArray(historicalData) || historicalData.length < 3) {
    return {
      trend: 'insufficient_data',
      strength: 'unknown',
      forecast: [],
      insights: []
    }
  }

  const marginData = historicalData
    .filter(d => d.profitMargin != null)
    .map((d, index) => ({
      month: d.month || `Month ${index + 1}`,
      value: d.profitMargin,
      date: d.date || new Date()
    }))
    .sort((a, b) => new Date(a.date) - new Date(b.date))

  if (marginData.length < 3) {
    return {
      trend: 'insufficient_data',
      strength: 'unknown',
      forecast: [],
      insights: []
    }
  }

  const regression = calculateLinearRegression(marginData)
  const { slope, intercept, r2 } = regression

  let direction = 'stable'
  if (slope > 1) direction = 'improving'
  else if (slope < -1) direction = 'declining'

  let strength = 'weak'
  if (r2 > 0.6) strength = 'strong'
  else if (r2 > 0.3) strength = 'moderate'

  // Generate forecast
  const lastIndex = marginData.length - 1
  const forecast = []
  for (let i = 1; i <= 3; i++) {
    const forecastValue = slope * (lastIndex + i) + intercept
    forecast.push({
      month: `Forecast ${i}`,
      value: Math.round(forecastValue * 10) / 10, // Round to 1 decimal
      confidence: Math.round(r2 * 100)
    })
  }

  return {
    trend: direction,
    strength,
    slope: Math.round(slope * 10) / 10,
    r2: Math.round(r2 * 100),
    forecast,
    insights: generateMarginTrendInsights(direction, strength, slope, marginData),
    currentValue: marginData[marginData.length - 1]?.value || 0
  }
}

/**
 * Detect seasonal patterns in data
 */
export const detectSeasonalPatterns = (historicalData) => {
  if (!Array.isArray(historicalData) || historicalData.length < 6) {
    return {
      hasSeasonality: false,
      pattern: 'unknown',
      strength: 0,
      insights: []
    }
  }

  // Group data by month to detect seasonal patterns
  const monthlyData = {}
  historicalData.forEach(d => {
    if (d.date) {
      const month = new Date(d.date).getMonth()
      if (!monthlyData[month]) monthlyData[month] = []
      monthlyData[month].push(d.revenue || 0)
    }
  })

  // Calculate average revenue by month
  const monthlyAverages = {}
  Object.keys(monthlyData).forEach(month => {
    const values = monthlyData[month]
    monthlyAverages[month] = values.reduce((sum, val) => sum + val, 0) / values.length
  })

  const averages = Object.values(monthlyAverages)
  if (averages.length < 3) {
    return {
      hasSeasonality: false,
      pattern: 'insufficient_data',
      strength: 0,
      insights: []
    }
  }

  // Calculate coefficient of variation to measure seasonality
  const mean = averages.reduce((sum, val) => sum + val, 0) / averages.length
  const variance = averages.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / averages.length
  const cv = Math.sqrt(variance) / mean

  const hasSeasonality = cv > 0.15 // 15% coefficient of variation threshold
  const strength = Math.min(cv * 100, 100)

  // Identify pattern type
  let pattern = 'irregular'
  const maxMonth = Object.keys(monthlyAverages).reduce((a, b) => 
    monthlyAverages[a] > monthlyAverages[b] ? a : b
  )
  const minMonth = Object.keys(monthlyAverages).reduce((a, b) => 
    monthlyAverages[a] < monthlyAverages[b] ? a : b
  )

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]

  if (hasSeasonality) {
    pattern = `Peak: ${monthNames[maxMonth]}, Low: ${monthNames[minMonth]}`
  }

  const insights = []
  if (hasSeasonality) {
    insights.push({
      type: 'seasonal',
      title: 'Seasonal Pattern Detected',
      message: `Revenue tends to be highest in ${monthNames[maxMonth]} and lowest in ${monthNames[minMonth]}.`,
      actionable: true
    })
  }

  return {
    hasSeasonality,
    pattern,
    strength: Math.round(strength),
    insights,
    monthlyAverages,
    peakMonth: monthNames[maxMonth],
    lowMonth: monthNames[minMonth]
  }
}

/**
 * Generate comprehensive business trend analysis
 */
export const generateComprehensiveTrendAnalysis = (historicalData) => {
  const revenueTrend = analyzeRevenueTrend(historicalData)
  const profitTrend = analyzeProfitMarginTrend(historicalData)
  const seasonalPattern = detectSeasonalPatterns(historicalData)

  // Calculate overall business momentum
  let momentum = 'stable'
  if (revenueTrend.direction === 'growing' && profitTrend.direction === 'improving') {
    momentum = 'accelerating'
  } else if (revenueTrend.direction === 'declining' && profitTrend.direction === 'declining') {
    momentum = 'decelerating'
  } else if (revenueTrend.direction === 'growing' || profitTrend.direction === 'improving') {
    momentum = 'mixed_positive'
  } else if (revenueTrend.direction === 'declining' || profitTrend.direction === 'declining') {
    momentum = 'mixed_negative'
  }

  return {
    revenue: revenueTrend,
    profitMargin: profitTrend,
    seasonality: seasonalPattern,
    momentum,
    overallInsights: generateOverallTrendInsights(revenueTrend, profitTrend, seasonalPattern, momentum)
  }
}

/**
 * Generate trend insights for revenue
 */
const generateTrendInsights = (direction, strength, slope, r2, data) => {
  const insights = []
  const monthlyGrowth = data.length > 1 ? Math.round(slope) : 0

  if (direction === 'growing' && strength === 'strong') {
    insights.push({
      type: 'positive',
      title: 'Strong Revenue Growth',
      message: `Revenue is growing consistently by approximately $${monthlyGrowth.toLocaleString()} per month.`,
      actionable: true
    })
  } else if (direction === 'declining' && strength === 'strong') {
    insights.push({
      type: 'warning',
      title: 'Declining Revenue Trend',
      message: `Revenue is declining by approximately $${Math.abs(monthlyGrowth).toLocaleString()} per month. Immediate action needed.`,
      actionable: true
    })
  } else if (strength === 'weak') {
    insights.push({
      type: 'neutral',
      title: 'Inconsistent Revenue Pattern',
      message: 'Revenue shows no clear trend. Focus on identifying growth opportunities.',
      actionable: true
    })
  }

  return insights
}

/**
 * Generate insights for profit margin trends
 */
const generateMarginTrendInsights = (direction, strength, slope, data) => {
  const insights = []

  if (direction === 'improving' && strength === 'strong') {
    insights.push({
      type: 'positive',
      title: 'Improving Profit Margins',
      message: 'Your profit margins are consistently improving, showing better operational efficiency.',
      actionable: false
    })
  } else if (direction === 'declining' && strength === 'strong') {
    insights.push({
      type: 'warning', 
      title: 'Declining Profit Margins',
      message: 'Profit margins are declining consistently. Review pricing and cost structure.',
      actionable: true
    })
  }

  return insights
}

/**
 * Generate overall trend insights
 */
const generateOverallTrendInsights = (revenue, profit, seasonality, momentum) => {
  const insights = []

  // Momentum insights
  if (momentum === 'accelerating') {
    insights.push({
      type: 'success',
      title: 'Business is Accelerating!',
      message: 'Both revenue and profitability are improving. Excellent business momentum.',
      priority: 'high'
    })
  } else if (momentum === 'decelerating') {
    insights.push({
      type: 'critical',
      title: 'Business Momentum Declining',
      message: 'Both revenue and profit margins are declining. Strategic intervention needed.',
      priority: 'critical'
    })
  }

  // Seasonality insights
  if (seasonality.hasSeasonality && seasonality.strength > 30) {
    insights.push({
      type: 'insight',
      title: 'Seasonal Business Pattern',
      message: `Plan for seasonal variations: ${seasonality.pattern}`,
      priority: 'medium'
    })
  }

  // Forecasting confidence
  const avgConfidence = (revenue.r2 + profit.r2) / 2
  if (avgConfidence < 40) {
    insights.push({
      type: 'info',
      title: 'Variable Performance',
      message: 'Business performance is highly variable. Focus on consistency.',
      priority: 'low'
    })
  }

  return insights
}